'use client';

import { useRef } from 'react';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER } from './bits';
import DossierHead from './DossierHead';
import { useChoreo } from './motion';

/**
 * §02 — Why teams pick rii (hairline band). Three cards; each mono footer
 * readout decodes in with a scramble once the card has entered — dossier
 * flavor for the numbers that carry the section's precision claims.
 */
export default function WhyRiiSection({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);

  useChoreo(rootRef, (root) => {
    const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } });
    tl.from(root.querySelectorAll('[data-why-card]'), {
      y: 26,
      opacity: 0,
      duration: 0.75,
      stagger: 0.12,
    });
    root.querySelectorAll<HTMLElement>('[data-readout]').forEach((el, i) => {
      tl.to(
        el,
        {
          duration: 0.9,
          scrambleText: { text: el.textContent ?? '', chars: '01·:x', speed: 0.4 },
        },
        0.35 + i * 0.12,
      );
    });
    return tl;
  });

  return (
    <section ref={rootRef} className="mt-16 border-y border-line-2 md:mt-24">
      <div className={`${CONTAINER} py-14 md:py-[88px]`}>
        <DossierHead num="02" label="WHY TEAMS PICK RII" title={data.why.title} />
        <div className="mt-8 flex flex-wrap gap-6 md:mt-11">
          {data.why.cards.map((card) => (
            <div
              key={card.title}
              data-why-card
              className="flex min-w-[260px] flex-[1_1_320px] flex-col gap-3 rounded-ref border border-line-2 bg-surface px-7 py-[26px]"
            >
              <h3 className="font-onest text-[17px] font-bold tracking-[-0.01em]">{card.title}</h3>
              <p className="text-[14.5px] leading-[1.65] text-pretty text-muted">{card.body}</p>
              <span
                data-readout
                className="mt-auto border-t border-line pt-2.5 font-jetbrains text-[10.5px] text-dim"
              >
                {card.readout}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
